// Button discovery for the setup wizard: the ESP watches every free pin with its
// pull-up on and reports debounced presses (stream event FE 09 pin); the pin that
// gets the user's presses is the button. Logic only; the dialog is in
// buttonwizard_ui.js.

import { CANDIDATE_PINS, PIN_MODE } from './device.js';

export const PRESSES_NEEDED = 3;

// Counts presses per pin and decides when one pin is clearly the button.
export class ButtonSearch {
  constructor(needed = PRESSES_NEEDED) {
    this.needed = needed;
    this.counts = new Map();
  }

  // Returns 'found' (this.pin is set), 'ambiguous' (counts are reset) or 'waiting'.
  press(pin) {
    this.counts.set(pin, (this.counts.get(pin) ?? 0) + 1);
    const ranked = [...this.counts.entries()].sort((a, b) => b[1] - a[1]);
    const [leader, n] = ranked[0];
    if (n < this.needed) return 'waiting';
    // Another pin that reacted almost as often means the presses are not clearly
    // one button's (two buttons, or a noisy wire): ask again instead of guessing.
    if (ranked[1] && ranked[1][1] >= this.needed - 1) {
      this.counts.clear();
      return 'ambiguous';
    }
    this.pin = leader;
    return 'found';
  }

  // Presses seen so far, most frequent first: [[pin, count], ...].
  get seen() {
    return [...this.counts.entries()].sort((a, b) => b[1] - a[1]);
  }
}

// step: 'ask' | 'press' | 'found' | 'saving' | 'failed'
export class ButtonWizard {
  // exclude: pins that must not be watched (the LED, the bus lines)
  constructor(device, { exclude = [], onChange = () => {} } = {}) {
    this.device = device;
    this.onChange = onChange;
    this.watch = [0, ...CANDIDATE_PINS].filter((p) => !exclude.includes(p));
    this.step = 'ask';
    this.search = new ButtonSearch();
    this.pin = null;
    this.error = null;
    this.hint = null;
    this.testing = false; // a pin test is running on the ESP
  }

  #set(step, error = null) {
    this.step = step;
    this.error = error;
    this.onChange(this);
  }

  // The user says they have a button: start watching.
  async start() {
    this.search = new ButtonSearch();
    this.hint = null;
    try {
      const status = await this.device.pinTest(PIN_MODE.WATCH, this.watch);
      if (status !== 0) throw new Error(`the device refused to watch the pins (status ${status})`);
      this.testing = true;
      this.#set('press');
    } catch (e) {
      this.#set('failed', e.message);
    }
  }

  // The device reports a press on `pin` (stream event FE 09).
  onPress(pin) {
    if (this.step !== 'press') return;
    const result = this.search.press(pin);
    if (result === 'found') {
      this.pin = this.search.pin;
      this.hint = null;
      this.#set('found');
    } else {
      this.hint =
        result === 'ambiguous' ? 'More than one pin reacted. Press only the button you want to use, three times.' : null;
      this.onChange(this);
    }
  }

  // The user has no button (or does not want one): stores "no button".
  async saveNoButton() {
    return this.#save(null);
  }

  async save() {
    if (this.step !== 'found') throw new Error('nothing to save yet');
    return this.#save(this.pin);
  }

  async #save(button) {
    this.#set('saving');
    try {
      const current = await this.device.getConfig();
      if (!current) throw new Error('could not read the current configuration');
      const status = await this.device.setConfig({ ...current.config, button, buttonDone: true });
      if (status !== 0) throw new Error(`the device refused the configuration (status ${status})`);
      this.testing = false; // the restart ends the test
    } catch (e) {
      this.#set('failed', e.message);
      throw e;
    }
  }

  // Back to the start of the presses (after a failure or a wrong pin).
  async retry() {
    await this.start();
  }

  // Leaves the wizard without saving: ends a running test (this restarts the ESP).
  async cancel() {
    if (!this.testing) return;
    this.testing = false;
    try {
      await this.device.pinTest(PIN_MODE.STOP);
    } catch {
      // the ESP restarts and may not answer
    }
  }
}
